import { z } from "zod";

import type { FetchScopedOpts } from "../state/useScopeData";

export const HELP_REQUEST_TYPES = ["help", "feedback", "issue", "idea"] as const;
export const HELP_REQUEST_URGENCIES = ["low", "medium", "high"] as const;

export type HelpRequestType = (typeof HELP_REQUEST_TYPES)[number];
export type HelpRequestUrgency = (typeof HELP_REQUEST_URGENCIES)[number];

export interface HelpRequestPayload {
  request_type: HelpRequestType;
  subject: string;
  description: string;
  urgency: HelpRequestUrgency;
  contact_me: boolean;
  context: HelpRequestContext;
}

export interface HelpRequestContext {
  page_url: string;
  route: string;
  workspace_id: string;
  workspace_name: string;
  client_id?: string;
  client_name?: string;
  app_version: string;
}

export interface HelpRequestInput {
  payload: HelpRequestPayload;
  idempotencyKey: string;
  attachment?: File;
}

export interface HelpRequestResponse {
  reference_id: string;
  status: "received";
  created_at: string;
}

export interface AskAnandMessage {
  role: "user" | "assistant";
  content: string;
}

export interface AskAnandChatResponse {
  text: string;
  grounded: boolean;
  handbook_section_ids: string[];
  suggested_followups: string[];
}

type ScopedFetch = (path: string, opts?: FetchScopedOpts) => Promise<unknown>;

const helpRequestResponseSchema = z.object({
  reference_id: z.string().trim().min(1),
  status: z.literal("received"),
  created_at: z.string().datetime({ offset: true }),
});

const askAnandChatResponseSchema = z.object({
  text: z.string().trim().min(1),
  grounded: z.boolean(),
  handbook_section_ids: z.array(z.string().trim().min(1)).max(4),
  suggested_followups: z.array(z.string().trim().min(1).max(240)).max(3),
});

export async function askAnand(
  fetchScoped: ScopedFetch,
  message: string,
  history: AskAnandMessage[],
  signal?: AbortSignal,
): Promise<AskAnandChatResponse> {
  const response = await fetchScoped("/help/chat", {
    init: {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message,
        history: history.slice(-6).map((item) => ({
          role: item.role,
          content: item.content.trim().slice(0, 2_000),
        })).filter((item) => item.content.length > 0),
      }),
      signal,
    },
  });
  return askAnandChatResponseSchema.parse(response);
}

/** Submit through the scope chokepoint. The browser sets the multipart boundary. */
export async function submitHelpRequest(
  fetchScoped: ScopedFetch,
  input: HelpRequestInput,
  signal?: AbortSignal,
): Promise<HelpRequestResponse> {
  const form = new FormData();
  form.append("payload", JSON.stringify(input.payload));
  if (input.attachment) form.append("attachment", input.attachment, input.attachment.name);

  const response = await fetchScoped("/help/requests", {
    init: {
      method: "POST",
      body: form,
      headers: { "Idempotency-Key": input.idempotencyKey },
      signal,
    },
  });

  return helpRequestResponseSchema.parse(response);
}
